import { getApiError } from '../../utils/errorHelper';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Package, Plus, Pencil, Trash2, Save, X, Loader2, FileText, FormInput, ChevronDown, ChevronUp, Check, ShieldCheck } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { productsApi, documentTypesApi, informationFieldsApi, configApi } from '../../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const emptyForm = {
  name: '',
  description: '',
  department_type: '',
  category: '',
  sub_category: '',
  product_type: 'one_time',
  duration_value: 12,
  duration_unit: 'months',
  time_to_complete_days: null,
  sac_code: '998231',
  quantity: 1,
  startease_fees: 0,
  statutory_fees: 0,
  inclusions: [],
  exclusions: [],
  duration_months: 12,
  document_collection_last_date: { day: 15, month: 5 },
  document_requirements: [],
  info_requirements: [],
  workflow_tag: '',
};

// Separate ProductForm component to prevent re-render issues
const ProductForm = ({ 
  initialData, 
  onSave, 
  onCancel, 
  isNew, 
  saving,
  documentTypes,
  informationFields,
  kycDocTypes,
  initialKycSlugs,
  productId
}) => {
  const [form, setForm] = useState(initialData || emptyForm);
  const [docDropdownOpen, setDocDropdownOpen] = useState(false);
  const [infoDropdownOpen, setInfoDropdownOpen] = useState(false);
  // KYC requirements: Set of "slug|entity_type|country" keys
  const [kycSelected, setKycSelected] = useState(new Set(initialKycSlugs || []));

  // Use refs to track input focus
  const inputRefs = useRef({});

  const handleFieldChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleDCLDChange = useCallback((field, value) => {
    setForm(prev => ({
      ...prev,
      document_collection_last_date: { ...(prev.document_collection_last_date || { day: 15, month: 5 }), [field]: parseInt(value) || 1 }
    }));
  }, []);

  const toggleDocumentRequirement = useCallback((docTypeId) => {
    setForm(prev => {
      const existing = prev.document_requirements.find(dr => dr.document_type_id === docTypeId);
      if (existing) {
        return { ...prev, document_requirements: prev.document_requirements.filter(dr => dr.document_type_id !== docTypeId) };
      } else {
        return { ...prev, document_requirements: [...prev.document_requirements, { document_type_id: docTypeId, is_required: true }] };
      }
    });
  }, []);

  const toggleDocumentRequiredFlag = useCallback((docTypeId) => {
    setForm(prev => ({
      ...prev,
      document_requirements: prev.document_requirements.map(dr =>
        dr.document_type_id === docTypeId ? { ...dr, is_required: !(dr.is_required ?? true) } : dr
      ),
    }));
  }, []);

  const toggleInfoRequirement = useCallback((fieldId) => {
    setForm(prev => {
      const existing = prev.info_requirements.find(ir => ir.information_field_id === fieldId);
      if (existing) {
        return { ...prev, info_requirements: prev.info_requirements.filter(ir => ir.information_field_id !== fieldId) };
      } else {
        return { ...prev, info_requirements: [...prev.info_requirements, { information_field_id: fieldId, is_required: true }] };
      }
    });
  }, []);

  const toggleInfoRequiredFlag = useCallback((fieldId) => {
    setForm(prev => ({
      ...prev,
      info_requirements: prev.info_requirements.map(ir =>
        ir.information_field_id === fieldId ? { ...ir, is_required: !(ir.is_required ?? true) } : ir
      ),
    }));
  }, []);

  const getServiceLabel = (type) => {
    const map = { gst: 'GST Filing', tds: 'TDS Filing', roc: 'ROC Filing', payroll: 'Payroll', license_renewal: 'License Renewal', company_registration: 'Registration', other: 'Other' };
    return map[type] || type || 'Unknown';
  };

  const getSelectedDocNames = () => {
    return form.document_requirements
      .map(dr => documentTypes.find(d => d.id === dr.document_type_id)?.name)
      .filter(Boolean);
  };

  const getSelectedInfoNames = () => {
    return form.info_requirements
      .map(ir => informationFields.find(f => f.id === ir.information_field_id)?.label)
      .filter(Boolean);
  };

  const handleSubmit = () => {
    onSave(form, Array.from(kycSelected));
  };

  return (
    <div className="admin-bg-inner/80 rounded-xl p-6 border border-emerald-500/30 space-y-6">
      <div className="flex items-center gap-2">
        <Package className="w-5 h-5 text-emerald-400" />
        <span className="text-[var(--admin-text)] font-semibold text-lg">{isNew ? 'Create New Product' : 'Edit Product'}</span>
      </div>
      
      {/* Basic Info */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold admin-text-dim uppercase tracking-wider">Basic Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs admin-text-dim block mb-1">Product Name *</label>
            <input 
              type="text"
              placeholder="e.g., Annual Return Filing (AOC-4)"
              value={form.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              className="w-full h-10 rounded-md admin-bg-card-inner border border-[var(--admin-border)] text-[var(--admin-text)] text-sm px-3 focus:outline-none focus:border-[var(--admin-green)]"
            />
          </div>
          <div>
            <label className="text-xs admin-text-dim block mb-1">Department Type</label>
            <input 
              type="text"
              placeholder="e.g., MCA, GST, Income Tax"
              value={form.department_type}
              onChange={(e) => handleFieldChange('department_type', e.target.value)}
              className="w-full h-10 rounded-md admin-bg-card-inner border border-[var(--admin-border)] text-[var(--admin-text)] text-sm px-3 focus:outline-none focus:border-[var(--admin-green)]"
            />
          </div>
          <div>
            <label className="text-xs admin-text-dim block mb-1">Workflow Tag</label>
            <input 
              type="text"
              placeholder="e.g. statutory_audit"
              value={form.workflow_tag}
              onChange={(e) => handleFieldChange('workflow_tag', e.target.value.replace(/\s+/g, '_').toLowerCase())}
              className="w-full h-10 rounded-md admin-bg-card-inner border border-[var(--admin-border)] text-[var(--admin-text)] text-sm px-3 focus:outline-none focus:border-[var(--admin-green)]"
              data-testid="product-workflow-tag-input"
            />
          </div>
        </div>
        
        <div>
          <label className="text-xs admin-text-dim block mb-1">Description *</label>
          <textarea
            placeholder="Detailed description of the product..."
            value={form.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            rows={3}
            className="w-full rounded-md admin-bg-card-inner border border-[var(--admin-border)] text-[var(--admin-text)] text-sm p-3 resize-none focus:outline-none focus:border-[var(--admin-green)]"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs admin-text-dim block mb-1">Category</label>
            <input 
              type="text"
              placeholder="e.g., Annual Compliance, ROC Filing"
              value={form.category}
              onChange={(e) => handleFieldChange('category', e.target.value)}
              className="w-full h-10 rounded-md admin-bg-card-inner border border-[var(--admin-border)] text-[var(--admin-text)] text-sm px-3 focus:outline-none focus:border-[var(--admin-green)]"
            />
          </div>
          <div>
            <label className="text-xs admin-text-dim block mb-1">Sub Category</label>
            <input 
              type="text"
              placeholder="e.g., AOC-4, MGT-7"
              value={form.sub_category}
              onChange={(e) => handleFieldChange('sub_category', e.target.value)}
              className="w-full h-10 rounded-md admin-bg-card-inner border border-[var(--admin-border)] text-[var(--admin-text)] text-sm px-3 focus:outline-none focus:border-[var(--admin-green)]"
            />
          </div>
          <div>
            <label className="text-xs admin-text-dim block mb-1">Time to Complete (Days)</label>
            <input 
              type="number"
              placeholder="e.g., 7"
              value={form.time_to_complete_days || ''}
              onChange={(e) => handleFieldChange('time_to_complete_days', e.target.value ? parseInt(e.target.value) : null)}
              className="w-full h-10 rounded-md admin-bg-card-inner border border-[var(--admin-border)] text-[var(--admin-text)] text-sm px-3 focus:outline-none focus:border-[var(--admin-green)]"
            />
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="space-y-4 pt-4 border-t border-[var(--admin-border)]">
        <h4 className="text-sm font-semibold admin-text-dim uppercase tracking-wider">Pricing Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs admin-text-dim block mb-1">SAC Code</label>
            <input 
              type="text"
              placeholder="998231"
              value={form.sac_code}
              onChange={(e) => handleFieldChange('sac_code', e.target.value)}
              className="w-full h-10 rounded-md admin-bg-card-inner border border-[var(--admin-border)] text-[var(--admin-text)] text-sm px-3 focus:outline-none focus:border-[var(--admin-green)]"
            />
          </div>
          <div>
            <label className="text-xs admin-text-dim block mb-1">Govt Fees (₹)</label>
            <input 
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={form.statutory_fees}
              onChange={(e) => handleFieldChange('statutory_fees', parseFloat(e.target.value) || 0)}
              className="w-full h-10 rounded-md admin-bg-card-inner border border-[var(--admin-border)] text-[var(--admin-text)] text-sm px-3 focus:outline-none focus:border-[var(--admin-green)]"
            />
          </div>
          <div>
            <label className="text-xs admin-text-dim block mb-1">Platform Fees (₹)</label>
            <input 
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={form.startease_fees}
              onChange={(e) => handleFieldChange('startease_fees', parseFloat(e.target.value) || 0)}
              className="w-full h-10 rounded-md admin-bg-card-inner border border-[var(--admin-border)] text-[var(--admin-text)] text-sm px-3 focus:outline-none focus:border-[var(--admin-green)]"
            />
          </div>
        </div>
        <div className="admin-bg-card-inner p-3 rounded-lg">
          <p className="text-sm admin-text-dim">
            Total Fees: <span className="text-emerald-400 font-semibold">
              ₹{(form.startease_fees + form.statutory_fees).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </span>
            <span className="text-xs admin-text-soft ml-2">
              (Govt Fees: ₹{form.statutory_fees} + Platform Fees: ₹{form.startease_fees})
            </span>
          </p>
        </div>
      </div>

      {/* Product Type */}
      <div className="space-y-4 pt-4 border-t border-[var(--admin-border)]">
        <h4 className="text-sm font-semibold admin-text-dim uppercase tracking-wider">Product Type</h4>
        <div className="flex gap-3">
          <button type="button"
            onClick={() => handleFieldChange('product_type', 'one_time')}
            className={`flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-all ${
              (form.product_type || 'one_time') === 'one_time'
                ? 'admin-btn-primary/20 border-[var(--admin-green)] admin-text-green'
                : 'admin-bg-card-inner border-[var(--admin-border)] admin-text-dim hover:border-[var(--admin-border)]'
            }`}
            data-testid="product-type-one-time"
          >
            One-Time
          </button>
          <button type="button"
            onClick={() => handleFieldChange('product_type', 'duration_based')}
            className={`flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-all ${
              form.product_type === 'duration_based'
                ? 'bg-emerald-600/20 border-emerald-500 text-emerald-400'
                : 'admin-bg-card-inner border-[var(--admin-border)] admin-text-dim hover:border-[var(--admin-border)]'
            }`}
            data-testid="product-type-duration"
          >
            Duration-Based
          </button>
        </div>

        {form.product_type === 'duration_based' && (
          <div className="admin-bg-card-inner rounded-lg p-4 border border-[var(--admin-border)] space-y-3">
            <label className="text-xs admin-text-dim block mb-1">Duration</label>
            <div className="flex gap-2">
              {[
                { label: '12 Months', value: 12, unit: 'months' },
                { label: '24 Months', value: 24, unit: 'months' },
                { label: '36 Months', value: 36, unit: 'months' },
              ].map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => { handleFieldChange('duration_value', opt.value); handleFieldChange('duration_unit', opt.unit); }}
                  className={`px-4 py-2 rounded-lg border text-xs font-medium transition-all ${
                    form.duration_unit === 'months' && form.duration_value === opt.value
                      ? 'bg-emerald-600/20 border-emerald-500 text-emerald-400'
                      : 'admin-bg-card-inner border-[var(--admin-border)] admin-text-dim hover:border-[var(--admin-border)]'
                  }`}
                  data-testid={`duration-${opt.value}m`}
                >
                  {opt.label}
                </button>
              ))}
              <button type="button"
                onClick={() => { handleFieldChange('duration_unit', 'days'); handleFieldChange('duration_value', form.duration_value || 365); }}
                className={`px-4 py-2 rounded-lg border text-xs font-medium transition-all ${
                  form.duration_unit === 'days'
                    ? 'bg-amber-600/20 border-amber-500 text-amber-400'
                    : 'admin-bg-card-inner border-[var(--admin-border)] admin-text-dim hover:border-[var(--admin-border)]'
                }`}
                data-testid="duration-custom"
              >
                Custom (Days)
              </button>
            </div>
            {form.duration_unit === 'days' && (
              <div className="mt-2">
                <label className="text-xs admin-text-dim block mb-1">Number of Days</label>
                <input type="number" min="1" max="3650"
                  value={form.duration_value || ''}
                  onChange={(e) => handleFieldChange('duration_value', parseInt(e.target.value) || 0)}
                  className="w-48 h-10 rounded-md admin-bg-card-inner border border-[var(--admin-border)] text-[var(--admin-text)] text-sm px-3 focus:outline-none focus:border-amber-500"
                  placeholder="e.g., 365"
                  data-testid="duration-days-input"
                />
              </div>
            )}
            <p className="text-xs admin-text-soft">A renewal order will be auto-generated 45 days before expiry.</p>
          </div>
        )}
      </div>

      {/* Inclusions & Exclusions */}
      <div className="space-y-4 pt-4 border-t border-[var(--admin-border)]">
        <h4 className="text-sm font-semibold admin-text-dim uppercase tracking-wider">What's Included & Not Included</h4>
        
        {/* Inclusions */}
        <div>
          <label className="text-xs text-emerald-400 block mb-2 font-medium uppercase tracking-wider">Inclusions (What's Covered)</label>
          {(form.inclusions || []).map((inc, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-2">
              <input
                type="text"
                placeholder="Title (e.g., Government filing fees)"
                value={inc.title}
                onChange={(e) => {
                  const updated = [...(form.inclusions || [])];
                  updated[idx] = { ...updated[idx], title: e.target.value };
                  handleFieldChange('inclusions', updated);
                }}
                className="flex-1 h-9 rounded-md admin-bg-card-inner border border-[var(--admin-border)] text-[var(--admin-text)] text-sm px-3 focus:outline-none focus:border-emerald-500"
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={inc.description || ''}
                onChange={(e) => {
                  const updated = [...(form.inclusions || [])];
                  updated[idx] = { ...updated[idx], description: e.target.value };
                  handleFieldChange('inclusions', updated);
                }}
                className="flex-1 h-9 rounded-md admin-bg-card-inner border border-[var(--admin-border)] text-[var(--admin-text)] text-sm px-3 focus:outline-none focus:border-emerald-500"
              />
              <button type="button" onClick={() => {
                const updated = (form.inclusions || []).filter((_, i) => i !== idx);
                handleFieldChange('inclusions', updated);
              }} className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => handleFieldChange('inclusions', [...(form.inclusions || []), { title: '', description: '' }])}
            className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 mt-1">
            <Plus className="w-3 h-3" /> Add inclusion
          </button>
        </div>

        {/* Exclusions */}
        <div>
          <label className="text-xs text-red-400 block mb-2 font-medium uppercase tracking-wider">Exclusions (What's NOT Covered)</label>
          {(form.exclusions || []).map((exc, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-2">
              <input
                type="text"
                placeholder="Title (e.g., Penalty charges)"
                value={exc.title}
                onChange={(e) => {
                  const updated = [...(form.exclusions || [])];
                  updated[idx] = { ...updated[idx], title: e.target.value };
                  handleFieldChange('exclusions', updated);
                }}
                className="flex-1 h-9 rounded-md admin-bg-card-inner border border-[var(--admin-border)] text-[var(--admin-text)] text-sm px-3 focus:outline-none focus:border-red-500"
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={exc.description || ''}
                onChange={(e) => {
                  const updated = [...(form.exclusions || [])];
                  updated[idx] = { ...updated[idx], description: e.target.value };
                  handleFieldChange('exclusions', updated);
                }}
                className="flex-1 h-9 rounded-md admin-bg-card-inner border border-[var(--admin-border)] text-[var(--admin-text)] text-sm px-3 focus:outline-none focus:border-red-500"
              />
              <button type="button" onClick={() => {
                const updated = (form.exclusions || []).filter((_, i) => i !== idx);
                handleFieldChange('exclusions', updated);
              }} className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => handleFieldChange('exclusions', [...(form.exclusions || []), { title: '', description: '' }])}
            className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 mt-1">
            <Plus className="w-3 h-3" /> Add exclusion
          </button>
        </div>
      </div>

      {/* Document Requirements - Multi-Select Dropdown */}
      <div className="space-y-4 pt-4 border-t border-[var(--admin-border)]">
        <h4 className="text-sm font-semibold admin-text-dim uppercase tracking-wider flex items-center gap-2">
          <FileText className="w-4 h-4" /> Document Requirements
        </h4>
        
        <div className="relative">
          <button
            type="button"
            onClick={() => setDocDropdownOpen(!docDropdownOpen)}
            className="w-full p-3 admin-bg-card-inner border border-[var(--admin-border)] rounded-lg text-left flex items-center justify-between hover:border-[var(--admin-border)] transition-colors"
          >
            <span className="text-sm admin-text-dim">
              {form.document_requirements.length > 0 
                ? `${form.document_requirements.length} document(s) selected`
                : 'Select required documents...'}
            </span>
            {docDropdownOpen ? <ChevronUp className="w-4 h-4 admin-text-dim" /> : <ChevronDown className="w-4 h-4 admin-text-dim" />}
          </button>
          
          {docDropdownOpen && (
            <div className="absolute z-20 w-full mt-1 admin-bg-card-inner border border-[var(--admin-border)] rounded-lg shadow-xl max-h-60 overflow-y-auto">
              {documentTypes.length === 0 ? (
                <p className="p-3 text-sm admin-text-soft">No document types available. Create some first.</p>
              ) : (
                documentTypes.map(dt => {
                  const isSelected = form.document_requirements.some(dr => dr.document_type_id === dt.id);
                  return (
                    <button
                      key={dt.id}
                      type="button"
                      onClick={() => toggleDocumentRequirement(dt.id)}
                      className={`w-full p-3 text-left flex items-center gap-3 hover:bg-[var(--admin-bg-elevated)] transition-colors ${isSelected ? 'bg-emerald-500/10' : ''}`}
                    >
                      <div className={`w-5 h-5 rounded border flex items-center justify-center ${isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-[var(--admin-border)]'}`}>
                        {isSelected && <Check className="w-3 h-3 text-[var(--admin-text)]" />}
                      </div>
                      <div>
                        <p className={`text-sm ${isSelected ? 'text-[var(--admin-text)]' : 'admin-text-dim'}`}>{dt.name}</p>
                        <p className="text-xs admin-text-soft">{dt.category}</p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>

        {form.document_requirements.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs admin-text-soft">Click a pill to toggle <span className="text-emerald-400">Required</span> ↔ <span className="text-amber-400">Optional</span>.</p>
            <div className="flex flex-wrap gap-2">
              {form.document_requirements.map((dr, i) => {
                const docName = (documentTypes.find(d => d.id === dr.document_type_id) || {}).name || 'Unknown';
                const isRequired = dr.is_required !== false;
                return (
                  <button
                    type="button"
                    key={dr.document_type_id || i}
                    onClick={() => toggleDocumentRequiredFlag(dr.document_type_id)}
                    className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                      isRequired
                        ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/25'
                        : 'bg-amber-500/15 text-amber-400 border-amber-500/40 hover:bg-amber-500/25'
                    }`}
                    data-testid={`doc-req-pill-${dr.document_type_id}`}
                    title={isRequired ? 'Click to make Optional' : 'Click to make Required'}
                  >
                    {docName} · {isRequired ? 'Required' : 'Optional'}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Information Requirements - Multi-Select Dropdown */}
      <div className="space-y-4 pt-4 border-t border-[var(--admin-border)]">
        <h4 className="text-sm font-semibold admin-text-dim uppercase tracking-wider flex items-center gap-2">
          <FormInput className="w-4 h-4" /> Information Requirements
        </h4>
        
        <div className="relative">
          <button
            type="button"
            onClick={() => setInfoDropdownOpen(!infoDropdownOpen)}
            className="w-full p-3 admin-bg-card-inner border border-[var(--admin-border)] rounded-lg text-left flex items-center justify-between hover:border-[var(--admin-border)] transition-colors"
          >
            <span className="text-sm admin-text-dim">
              {form.info_requirements.length > 0 
                ? `${form.info_requirements.length} field(s) selected`
                : 'Select required information fields...'}
            </span>
            {infoDropdownOpen ? <ChevronUp className="w-4 h-4 admin-text-dim" /> : <ChevronDown className="w-4 h-4 admin-text-dim" />}
          </button>
          
          {infoDropdownOpen && (
            <div className="absolute z-20 w-full mt-1 admin-bg-card-inner border border-[var(--admin-border)] rounded-lg shadow-xl max-h-60 overflow-y-auto">
              {informationFields.length === 0 ? (
                <p className="p-3 text-sm admin-text-soft">No information fields available. Create some first.</p>
              ) : (
                informationFields.map(field => {
                  const isSelected = form.info_requirements.some(ir => ir.information_field_id === field.id);
                  return (
                    <button
                      key={field.id}
                      type="button"
                      onClick={() => toggleInfoRequirement(field.id)}
                      className={`w-full p-3 text-left flex items-center gap-3 hover:bg-[var(--admin-bg-elevated)] transition-colors ${isSelected ? 'bg-indigo-500/10' : ''}`}
                    >
                      <div className={`w-5 h-5 rounded border flex items-center justify-center ${isSelected ? 'bg-indigo-500 border-[var(--admin-green)]' : 'border-[var(--admin-border)]'}`}>
                        {isSelected && <Check className="w-3 h-3 text-[var(--admin-text)]" />}
                      </div>
                      <div>
                        <p className={`text-sm ${isSelected ? 'text-[var(--admin-text)]' : 'admin-text-dim'}`}>{field.label}</p>
                        <p className="text-xs admin-text-soft">{field.field_type} • {field.category}</p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>

        {form.info_requirements.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs admin-text-soft">Click a pill to toggle <span className="text-emerald-400">Required</span> ↔ <span className="text-amber-400">Optional</span>.</p>
            <div className="flex flex-wrap gap-2">
              {form.info_requirements.map((ir, i) => {
                const fldName = (informationFields.find(f => f.id === ir.information_field_id) || {}).label || 'Unknown';
                const isRequired = ir.is_required !== false;
                return (
                  <button
                    type="button"
                    key={ir.information_field_id || i}
                    onClick={() => toggleInfoRequiredFlag(ir.information_field_id)}
                    className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                      isRequired
                        ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/25'
                        : 'bg-amber-500/15 text-amber-400 border-amber-500/40 hover:bg-amber-500/25'
                    }`}
                    data-testid={`info-req-pill-${ir.information_field_id}`}
                    title={isRequired ? 'Click to make Optional' : 'Click to make Required'}
                  >
                    {fldName} · {isRequired ? 'Required' : 'Optional'}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>


      {/* Smart KYC Requirements — Two-column India-only layout */}
      <div className="space-y-4 pt-4 border-t border-[var(--admin-border)]">
        <h4 className="text-sm font-semibold admin-text-dim uppercase tracking-wider flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> Smart KYC Requirements
        </h4>
        <p className="text-xs admin-text-soft">Select which KYC documents are required for this product. These determine the KYC gap for orders.</p>
        
        {kycDocTypes.company_documents || kycDocTypes.individual_documents ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left: Company Documents */}
            <div className="admin-bg-card-inner rounded-lg p-3 border border-[var(--admin-border)]">
              <p className="text-xs admin-text-soft uppercase tracking-wider font-medium mb-2">Company Documents</p>
              <div className="space-y-1.5">
                {(kycDocTypes.company_documents || []).map(dt => {
                  const key = `${dt.code}|company|IN`;
                  const isSelected = kycSelected.has(key);
                  return (
                    <button key={dt.code} type="button" onClick={() => {
                      setKycSelected(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
                    }} className={`w-full p-2 text-left flex items-center gap-2.5 rounded-lg hover:bg-[var(--admin-bg-elevated)] transition-colors ${isSelected ? 'bg-emerald-500/10 border border-emerald-500/30' : 'border border-transparent'}`}>
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-[var(--admin-border)]'}`}>
                        {isSelected && <Check className="w-2.5 h-2.5 text-[var(--admin-text)]" />}
                      </div>
                      <span className={`text-sm ${isSelected ? 'text-[var(--admin-text)]' : 'admin-text-dim'}`}>{dt.name}</span>
                      {dt.required && <span className="text-[9px] text-amber-400 bg-amber-500/10 px-1 rounded">Required</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Right: Individual/Director Documents */}
            <div className="admin-bg-card-inner rounded-lg p-3 border border-[var(--admin-border)]">
              <p className="text-xs admin-text-soft uppercase tracking-wider font-medium mb-2">Director / Individual Documents</p>
              <div className="space-y-1.5">
                {(kycDocTypes.individual_documents || []).map(dt => {
                  const key = `${dt.code}|director|IN`;
                  const isSelected = kycSelected.has(key);
                  return (
                    <button key={dt.code} type="button" onClick={() => {
                      setKycSelected(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
                    }} className={`w-full p-2 text-left flex items-center gap-2.5 rounded-lg hover:bg-[var(--admin-bg-elevated)] transition-colors ${isSelected ? 'bg-emerald-500/10 border border-emerald-500/30' : 'border border-transparent'}`}>
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-[var(--admin-border)]'}`}>
                        {isSelected && <Check className="w-2.5 h-2.5 text-[var(--admin-text)]" />}
                      </div>
                      <span className={`text-sm ${isSelected ? 'text-[var(--admin-text)]' : 'admin-text-dim'}`}>{dt.name}</span>
                      {dt.required && <span className="text-[9px] text-amber-400 bg-amber-500/10 px-1 rounded">Required</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm admin-text-soft p-3 admin-bg-card-inner rounded-lg">Loading KYC document types...</p>
        )}
        {kycSelected.size > 0 && (
          <p className="text-xs text-emerald-400">{kycSelected.size} KYC requirement(s) selected</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-[var(--admin-border)]">
        <Button onClick={handleSubmit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> {isNew ? 'Create Product' : 'Update Product'}</>}
        </Button>
        <Button variant="ghost" onClick={onCancel} className="admin-text-dim hover:text-[var(--admin-text)]">
          <X className="w-4 h-4 mr-2" /> Cancel
        </Button>
      </div>
    </div>
  );
};

// Main ProductsTab component
const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [informationFields, setInformationFields] = useState([]);
  const [kycDocTypes, setKycDocTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [productOrders, setProductOrders] = useState({});
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingKycSlugs, setEditingKycSlugs] = useState(new Set());

  useEffect(() => {
    Promise.all([
      loadProducts(),
      loadDocumentTypes(),
      loadInformationFields(),
      loadKycDocTypes()
    ]);
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await productsApi.getAllAdmin(true);
      setProducts(data || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentTypes = async () => {
    try {
      const data = await documentTypesApi.getAll();
      setDocumentTypes(data || []);
    } catch (error) {
      console.error('Failed to load document types', error);
    }
  };

  const loadInformationFields = async () => {
    try {
      const data = await informationFieldsApi.getAll();
      setInformationFields(data || []);
    } catch (error) {
      console.error('Failed to load information fields', error);
    }
  };

  const loadKycDocTypes = async () => {
    try {
      const data = await configApi.getKYCDocumentTypes('IN');
      setKycDocTypes(data || {});
    } catch (error) {
      console.error('Failed to load KYC doc types', error);
    }
  };

  const handleCreateNew = () => {
    setEditingId(null);
    setEditingProduct(null);
    setEditingKycSlugs(new Set());
    setShowForm(true);
  };

  const handleEdit = async (product) => {
    setEditingId(product.id);
    setEditingProduct({
      name: product.name || '',
      description: product.description || '',
      department_type: product.department_type || '',
      category: product.category || '',
      sub_category: product.sub_category || '',
      time_to_complete_days: product.time_to_complete_days || null,
      sac_code: product.sac_code || '998231',
      quantity: product.quantity || 1,
      startease_fees: product.startease_fees || 0,
      statutory_fees: product.statutory_fees || 0,
      inclusions: product.inclusions || [],
      exclusions: product.exclusions || [],
      service_type: product.service_type || '',
      duration_months: product.duration_months || 12,
      document_collection_last_date: product.document_collection_last_date || { day: 15, month: 5 },
      document_requirements: product.document_requirements || [],
      info_requirements: product.info_requirements || [],
      workflow_tag: product.workflow_tag || '',
    });

    // Load existing KYC requirements
    try {
      const kycData = await productsApi.getKycRequirements(product.id);
      const slugs = (kycData?.requirements || []).map(r => `${r.document_type_slug}|${r.entity_type}|${r.country}`);
      setEditingKycSlugs(new Set(slugs));
    } catch {
      setEditingKycSlugs(new Set());
    }

    setShowForm(true);
  };

  const handleSave = async (formData, kycReqs = []) => {
    if (!formData.name?.trim() || !formData.description?.trim()) {
      toast.error('Name and description are required');
      return;
    }

    setSaving(true);
    try {
      let savedProductId = editingId;
      if (editingId) {
        await productsApi.update(editingId, formData);
        toast.success('Product updated');
      } else {
        const created = await productsApi.create(formData);
        savedProductId = created.id;
        toast.success('Product created');
      }

      // Save KYC requirements - convert "slug|entity_type|country" keys to objects
      if (savedProductId) {
        const kycPayload = kycReqs.map(key => {
          const [document_type_slug, entity_type, country] = key.split('|');
          return { document_type_slug, entity_type, country };
        });
        await productsApi.saveKycRequirements(savedProductId, kycPayload);
      }

      setShowForm(false);
      setEditingId(null);
      setEditingProduct(null);
      setEditingKycSlugs(new Set());
      loadProducts();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setEditingProduct(null);
    setEditingKycSlugs(new Set());
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this product? It will be hidden from clients but can be reactivated.')) return;
    try {
      await productsApi.delete(id);
      toast.success('Product deactivated');
      loadProducts();
    } catch (error) {
      toast.error('Failed to deactivate');
    }
  };

  const handleHardDelete = async (id) => {
    if (!window.confirm('PERMANENTLY DELETE this product? This cannot be undone. Only works if no orders exist for it.')) return;
    try {
      await productsApi.hardDelete(id);
      toast.success('Product permanently deleted');
      loadProducts();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to delete permanently'));
    }
  };

  const getServiceLabel = (type) => {
    const map = { gst: 'GST Filing', tds: 'TDS Filing', roc: 'ROC Filing', payroll: 'Payroll', license_renewal: 'License Renewal', company_registration: 'Registration', other: 'Other' };
    return map[type] || type || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[var(--admin-text)]">Products</h2>
          <p className="admin-text-dim text-sm">Configure service products with document & info requirements</p>
        </div>
        {!showForm && (
          <Button onClick={handleCreateNew} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" /> Add Product
          </Button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <ProductForm
          initialData={editingProduct}
          onSave={handleSave}
          onCancel={handleCancel}
          isNew={!editingId}
          saving={saving}
          documentTypes={documentTypes}
          informationFields={informationFields}
          kycDocTypes={kycDocTypes}
          initialKycSlugs={editingKycSlugs}
          productId={editingId}
        />
      )}

      {/* Products List */}
      {!showForm && (
        <div className="space-y-3">
          {products.length === 0 ? (
            <div className="text-center py-12 admin-text-soft">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No products yet. Create your first one!</p>
            </div>
          ) : (
            products.map(product => {
              const serviceLabel = getServiceLabel(product.service_type);
              const isExpanded = expandedId === product.id;
              const totalPrice = (product.startease_fees || 0) + (product.statutory_fees || 0);
              
              return (
                <div key={product.id} className="admin-card overflow-hidden">
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-[var(--admin-bg-elevated)] transition-colors"
                    onClick={async () => {
                      if (isExpanded) {
                        setExpandedId(null);
                      } else {
                        setExpandedId(product.id);
                        if (!productOrders[product.id]) {
                          try {
                            const res = await fetch(`${API_URL}/api/admin/products/${product.id}/orders`, {
                              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                            });
                            const data = await res.json();
                            setProductOrders(prev => ({ ...prev, [product.id]: data.orders || [] }));
                          } catch { /* ignore */ }
                        }
                      }
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-500/20">
                        <Package className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-[var(--admin-text)] font-medium">{product.name}</h3>
                          {product.is_paused && <span className="text-xs bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">Paused</span>}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs admin-text-soft">{product.department_type || 'General'}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            product.product_type === 'duration_based' 
                              ? 'bg-emerald-500/15 text-emerald-400' 
                              : 'bg-indigo-500/15 admin-text-green'
                          }`}>
                            {product.product_type === 'duration_based' ? 'Duration-Based' : 'One-Time'}
                          </span>
                          <span className="text-xs text-amber-400">
                            ₹{totalPrice.toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs admin-text-soft mr-4">
                        {product.document_requirements?.length || 0} docs • {product.info_requirements?.length || 0} fields
                      </span>
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleEdit(product); }} className="admin-text-dim hover:text-[var(--admin-text)]">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDelete(product.id); }} className="admin-text-dim hover:text-amber-400" title="Deactivate (soft delete)">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleHardDelete(product.id); }} className="admin-text-dim hover:text-red-500" title="Permanently delete">
                        <X className="w-4 h-4" />
                      </Button>
                      {isExpanded ? <ChevronUp className="w-5 h-5 admin-text-dim" /> : <ChevronDown className="w-5 h-5 admin-text-dim" />}
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div className="border-t border-[var(--admin-border)] p-4 admin-bg-inner">
                      <p className="admin-text-dim text-sm mb-4">{product.description}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="admin-bg-card-inner p-3 rounded-lg">
                          <p className="text-xs admin-text-soft mb-1">Category</p>
                          <p className="text-[var(--admin-text)] text-sm">{product.category || '-'}</p>
                        </div>
                        <div className="admin-bg-card-inner p-3 rounded-lg">
                          <p className="text-xs admin-text-soft mb-1">Sub Category</p>
                          <p className="text-[var(--admin-text)] text-sm">{product.sub_category || '-'}</p>
                        </div>
                        <div className="admin-bg-card-inner p-3 rounded-lg">
                          <p className="text-xs admin-text-soft mb-1">SAC Code</p>
                          <p className="text-[var(--admin-text)] text-sm">{product.sac_code || '-'}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h5 className="text-xs font-semibold admin-text-dim uppercase mb-2">Document Requirements</h5>
                          {product.document_requirements?.length > 0 ? (
                            <ul className="space-y-1">
                              {product.document_requirements.map((dr, i) => {
                                const dt = documentTypes.find(d => d.id === dr.document_type_id);
                                return dt ? (
                                  <li key={i} className="text-sm admin-text-dim flex items-center gap-2">
                                    <FileText className="w-3 h-3 text-emerald-400" /> {dt.name}
                                  </li>
                                ) : null;
                              })}
                            </ul>
                          ) : (
                            <p className="text-sm admin-text-soft">No documents required</p>
                          )}
                        </div>
                        <div>
                          <h5 className="text-xs font-semibold admin-text-dim uppercase mb-2">Information Requirements</h5>
                          {product.info_requirements?.length > 0 ? (
                            <ul className="space-y-1">
                              {product.info_requirements.map((ir, i) => {
                                const field = informationFields.find(f => f.id === ir.information_field_id);
                                return field ? (
                                  <li key={i} className="text-sm admin-text-dim flex items-center gap-2">
                                    <FormInput className="w-3 h-3 admin-text-green" /> {field.label}
                                  </li>
                                ) : null;
                              })}
                            </ul>
                          ) : (
                            <p className="text-sm admin-text-soft">No info fields required</p>
                          )}
                        </div>
                      </div>

                      {/* Orders for this product */}
                      <div className="mt-4 pt-4 border-t border-[var(--admin-border)]">
                        <h5 className="text-xs font-semibold admin-text-dim uppercase mb-3">Orders ({(productOrders[product.id] || []).length})</h5>
                        {(productOrders[product.id] || []).length > 0 ? (
                          <div className="space-y-1.5 max-h-60 overflow-y-auto">
                            {(productOrders[product.id] || []).map(order => (
                              <div key={order.id} className="flex items-center justify-between py-2 px-3 admin-field text-sm" data-testid={`product-order-${order.id}`}>
                                <div className="flex items-center gap-3">
                                  <span className="text-[var(--admin-text)] font-medium truncate max-w-[200px]">{order.company_name}</span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    order.status === 'completed' ? 'bg-emerald-500/15 text-emerald-400' :
                                    order.status === 'under_review' ? 'bg-blue-500/15 text-blue-400' :
                                    order.status === 'payment_pending' ? 'bg-amber-500/15 text-amber-400' :
                                    'bg-slate-600/20 admin-text-dim'
                                  }`}>
                                    {(order.status || 'draft').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                  </span>
                                </div>
                                <span className="text-xs admin-text-soft">{order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm admin-text-soft">No orders yet for this product</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default ProductsTab;
